import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import HumanResourcesHeader from 'routes/HumanResourcesModule/HumanResourcesHome/HumanResourcesHeader';
import SynthesisHeader from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/components/SynthesisHeader/SynthesisHeader';
import { StyBackgroundLessTabulatedChip } from 'routes/HumanResourcesModule/components/ui/StyTabulatedChips';
import { useGetPayState } from 'routes/HumanResourcesModule/PayState/helpers/hooks/payState';
import SupCompAmounts from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/SupCompAmounts/SupCompAmounts';
import Absence from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/Absence/Absence';
import EchelonGrid from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/Echelon/EchelonGrid';
import VacantGrid from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/Vacant/VacantGrid';
import SynthesisHeaderLoader from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/components/SynthesisHeader/SynthesisHeaderLoader';
import NotFound from 'components/Errors/NotFound';
import StyContentWrapper from 'routes/HumanResourcesModule/components/ui/StyContentWrapper';
import { useTranslation } from 'react-i18next';
import StyHrPageContainer from 'routes/HumanResourcesModule/components/ui/StyHrPageContainer';
import { HR_HOME_PAGE_URL } from 'components/App/components/routes';
import PayComponentSalaryMassGrid from './PayComponentSalaryMass/PayComponentSalaryMassGrid';
import HelperAlert from 'ui/HelperAlert';
import PermanentSalaryMass from './SalaryMass/PermanentSalaryMass';
import NonPermanentSalaryMass from './SalaryMass/NonPermanentSalaryMass';
import OrgChartChips from 'routes/HumanResourcesModule/components/OrgChartChips';
import { PayStateIndicators } from './PayStateIndicators/PayStateIndicators';

const StyGridsWrapper = styled(Box, {
    name: 'StyGridsWrapper',
})({
    display: 'flex',
    flexDirection: 'column',
    gap: '2em',
    margin: '2em 0',
});

const StyMarginLessTabulatedChip = styled(StyBackgroundLessTabulatedChip, {
    name: 'StyBackgroundLessTabulatedChip',
})(() => ({
    backgroundColor: 'transparent',
    '&.MuiTabs-root': {
        marginLeft: 0,
    },
    '&.MuiBox-root': {
        paddingLeft: 0,
    },
}));

const StyHelpBox = styled(HelperAlert, { name: 'StyHelpBox' })(() => ({
    marginTop: '24px',
    marginBottom: '24px',
}));

function LoadingPage(): React.ReactElement {
    const { t } = useTranslation();
    return (
        <StyHrPageContainer className="page-container dx-viewport">
            <HumanResourcesHeader title={t('header.payState')} />
            <StyContentWrapper>
                <Box sx={{ width: '100%' }}>
                    <SynthesisHeaderLoader />
                </Box>
            </StyContentWrapper>
        </StyHrPageContainer>
    );
}

export default function PayStateSynthesis(): React.ReactElement {
    const { t } = useTranslation();
    const { id = '' } = useParams();
    const currentYear = new Date().getFullYear();
    const [shouldRedirect, setShouldRedirect] = useState(false);

    const {
        data: payStateData,
        isLoading: isPayStateLoading,
        isError: isPayStateError,
    } = useGetPayState(parseInt(id, 10));

    // if an error occur on the getPayState request
    // the user is redirected to the homepage after 5sc.
    useEffect(() => {
        if (!isPayStateLoading && isPayStateError) {
            setTimeout(() => {
                setShouldRedirect(true);
            }, 5000);
        }
    }, [isPayStateError, isPayStateLoading]);

    if (isPayStateLoading) {
        return <LoadingPage />;
    }
    const tabsList = [
        {
            label: t('humanResources.payState.salaryMassStudy'),
            children: (
                <StyGridsWrapper>
                    <PayStateIndicators payState={payStateData!} />
                    <PermanentSalaryMass payState={payStateData!} />
                    <SupCompAmounts payState={payStateData!} />
                    <NonPermanentSalaryMass payState={payStateData!} />
                    <PayComponentSalaryMassGrid
                        payState={payStateData!}
                        permanent
                    />
                    <PayComponentSalaryMassGrid payState={payStateData!} />
                    <EchelonGrid payState={payStateData!} />
                </StyGridsWrapper>
            ),
            pathname: `${HR_HOME_PAGE_URL}/pay_state/${id}/diary`,
            disabled: false,
            key: 'payDiary',
        },
        {
            label: t('humanResources.positionsOccupations'),
            children: (
                <StyGridsWrapper>
                    {/*
                    We hide this grid for now because the data is not ready yet.
                    <ETPTGrid payState={payStateData!} />
                    */}
                    <Absence payState={payStateData!} />
                    <VacantGrid payState={payStateData!} />
                </StyGridsWrapper>
            ),
            pathname: `${HR_HOME_PAGE_URL}/pay_state/${id}/occupations`,
            disabled: false,
            key: 'occupations',
        },
    ];

    return (
        <StyHrPageContainer
            className="page-container dx-viewport"
            sx={{ display: 'flex', flexDirection: 'column' }}
        >
            <Box sx={{ flex: 0 }}>
                <HumanResourcesHeader title={t('header.payState')} />
            </Box>
            <StyContentWrapper>
                <Box sx={{ width: '100%' }}>
                    {isPayStateLoading ? (
                        <SynthesisHeaderLoader />
                    ) : (
                        <>
                            {isPayStateError ? (
                                <>
                                    {shouldRedirect ? (
                                        <Navigate
                                            to={`/human_resources/pay_state_home/${currentYear}`}
                                            replace
                                        />
                                    ) : (
                                        <NotFound />
                                    )}
                                </>
                            ) : (
                                <>
                                    <SynthesisHeader payState={payStateData!} />

                                    <StyHelpBox>
                                        <Typography>
                                            {t('humanResources.payState.help')}
                                        </Typography>
                                    </StyHelpBox>

                                    {payStateData && (
                                        <OrgChartChips item={payStateData} />
                                    )}
                                    <Box sx={{ width: '100%' }}>
                                        <StyMarginLessTabulatedChip
                                            tabsList={tabsList}
                                        />
                                    </Box>
                                </>
                            )}
                        </>
                    )}
                </Box>
            </StyContentWrapper>
        </StyHrPageContainer>
    );
}
